import React from 'react';
import {
  Gauge, Lightning, ShieldCheck, Bell,
  ChartLineUp, Cube, X, MapTrifold, Notebook, GearSix, Scales, Factory, Cloud, Compass
} from '@phosphor-icons/react';
import { useTranslation } from '../i18n/LanguageContext';

const NAV_ITEMS = [
  { id: 'cockpit', icon: Gauge, labelKey: 'sidebar.cockpit' },
  { id: 'heatmap', icon: MapTrifold, labelKey: 'sidebar.heatmap' },
  { id: 'twin', icon: Cube, labelKey: 'sidebar.twin' },
  { id: 'emissions', icon: Cloud, labelKey: 'sidebar.emissions' },
  { id: 'oee', icon: Factory, labelKey: 'sidebar.oee' },
  { id: 'cbam', icon: Scales, labelKey: 'sidebar.cbam' },
  { id: 'reports', icon: Notebook, labelKey: 'sidebar.reports' },
  { id: 'governance', icon: ShieldCheck, labelKey: 'sidebar.governance' },
  { id: 'alerts', icon: Bell, labelKey: 'sidebar.alerts' },
  { id: 'admin', icon: GearSix, labelKey: 'sidebar.admin' },
];

const ROLE_NAV_ACCESS = {
  Executive: ['cockpit', 'heatmap', 'twin', 'emissions', 'oee', 'cbam', 'reports', 'governance', 'alerts', 'admin'],
  Operations: ['cockpit', 'heatmap', 'twin', 'emissions', 'oee', 'cbam', 'reports', 'alerts'],
  Sustainability: ['cockpit', 'heatmap', 'emissions', 'oee', 'cbam', 'reports', 'governance', 'alerts'],
  Finance: ['cockpit', 'heatmap', 'alerts'],
};

export default function Sidebar({ activeView, setActiveView, isOpen, onClose, userRole, onStartTour }) {
  const { t } = useTranslation();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside
        data-testid="sidebar"
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-[#1A2015] flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-white.svg" alt="SusTek" className="w-9 h-9" />
            <div>
              <span className="font-heading text-lg font-bold text-white tracking-tight">SusTek</span>
              <span className="text-[10px] text-tertiary align-super ml-0.5">TM</span>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden text-tertiary hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Role badge */}
        <div className="px-6 mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/30 rounded-full text-[11px] font-body font-medium text-sage uppercase tracking-wider">
            <Lightning size={12} weight="fill" />
            {t('sidebar.badge')}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-1">
          {NAV_ITEMS.filter(item => {
            const allowed = ROLE_NAV_ACCESS[userRole] || ROLE_NAV_ACCESS['Executive'];
            return allowed.includes(item.id);
          }).map((item) => {
            const Icon = item.icon;
            const active = activeView === item.id;
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => { setActiveView(item.id); onClose(); }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm
                  transition-all duration-200
                  ${active
                    ? 'bg-primary text-white font-medium'
                    : 'text-[#A3A380] hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <Icon size={20} weight={active ? 'fill' : 'duotone'} />
                {t(item.labelKey)}
              </button>
            );
          })}
        </nav>

        {/* Take Tour button */}
        <div className="px-3 mb-2">
          <button
            data-testid="take-tour-btn"
            onClick={() => { onStartTour?.(); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[#A3A380] hover:bg-white/5 hover:text-white transition-all duration-200 text-sm font-body"
          >
            <Compass size={18} weight="duotone" />
            {t('sidebar.takeTour')}
          </button>
        </div>

        {/* Bottom section */}
        <div className="p-4 m-3 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <ChartLineUp size={16} className="text-sage" />
            <span className="text-xs font-body font-semibold text-sage uppercase tracking-wider">{t('sidebar.systemStatus')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse-glow" />
            <span className="text-xs font-body text-[#A3A380]">{t('sidebar.allOperational')}</span>
          </div>
        </div>
      </aside>
    </>
  );
}
